import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useCallback } from "react";

import x_api_key from "@/keyfile";

import { FITCHECK_SERVICE_URL } from ".";

/**
 * Role explainers and fit checks do not change once generated, so we cache them
 * locally for one hour to avoid repeated API calls when a user re-requests them.
 */
const ONE_HOUR = 60 * 60 * 1000;

const fitcheckAPI = axios.create({
	baseURL: FITCHECK_SERVICE_URL,
	headers: {
		"x-api-key": x_api_key,
		"Content-Type": "application/json",
	},
});

// Type definitions based on Zod schema
type FitnessStatus = "MEET" | "PARTIAL" | "MISSING";

interface BaselineItem {
	status: FitnessStatus;
	strengths?: string[];
	gaps?: string[];
	recommendations?: string;
}

interface BaselineCheck {
	education: BaselineItem;
	experience: BaselineItem;
	skills: BaselineItem;
	certification: BaselineItem;
}

interface DecisionGuidance {
	ifApplyNow: [string, string, string];
	ifCloseGapsFirst: [string, string, string];
}

export interface FitCheckAssessment {
	roleTitle: string;
	company?: string;
	baselineCheck: BaselineCheck;
	strengths: string[];
	gaps: string[];
	focusAreas: [string, string, string];
	decisionGuidance: DecisionGuidance;
	encouragementNote: string;
}

export interface FitCheckResponse {
	userId: string;
	jobId: string;
	fitCheck: FitCheckAssessment;
	createdAt: string;
	updatedAt: string;
}

export interface RoleExplainerResponse {
	jobId: string;
	roleExplainer: FitCheckAssessment;
	createdAt: string;
	updatedAt: string;
}

interface UserData {
	firstName: string;
	lastName: string;
	email: string;
	userId: string;
}

interface CreateFitCheckPayload {
	userId: string;
	jobId: string;
	resumeURL: string;
	user: UserData;
	jobDetails: Record<string, unknown>;
}

interface CreateRoleExplainerPayload {
	jobDetails: Record<string, unknown>;
	jobId: string;
	userId: string;
}

export function useFitCheck(userId: string, jobId: string) {
	return useQuery<FitCheckResponse>({
		queryKey: ["fitcheck", userId, jobId],
		queryFn: async () => {
			const { data } = await fitcheckAPI.get<FitCheckResponse>(`/fit-check?userId=${userId}&jobId=${jobId}`);
			return data;
		},
		enabled: !!userId && !!jobId,
		retryDelay: 100,
		staleTime: ONE_HOUR,
		gcTime: ONE_HOUR,
	});
}

// mutation to create a fitcheck request
export function useCreateFitCheckMutation() {
	const queryClient = useQueryClient();
	return useMutation<FitCheckResponse, Error, CreateFitCheckPayload>({
		mutationFn: async ({ userId, jobId, resumeURL, user, jobDetails }: CreateFitCheckPayload) => {
			const { data } = await fitcheckAPI.post<FitCheckResponse>("/fit-check", {
				userId,
				jobId,
				resumeURL,
				user,
				jobDetails,
			});
			return data;
		},
		onSuccess: (data) => {
			// Prime the cache so a later useFitCheck read serves this result for one hour.
			queryClient.setQueryData(["fitcheck", data.userId, data.jobId], data);
		},
	});
}

interface FitChecksListResponse {
	success: boolean;
	userId: string;
	count: number;
	fitChecks: FitCheckResponse[];
}

// get fitcheck history for a user "/fit-checks/:userId"
export function useFitChecks(userId: string) {
	return useQuery<FitCheckResponse[]>({
		queryKey: ["fit-checks", userId],
		queryFn: async () => {
			const { data } = await fitcheckAPI.get<FitChecksListResponse>(`/fit-checks/${userId}`);
			return data.fitChecks;
		},
		enabled: !!userId,
	});
}

async function fetchRoleExplainer({ jobDetails, jobId }: CreateRoleExplainerPayload) {
	const { data } = await fitcheckAPI.post<RoleExplainerResponse>("/role-explainer", {
		jobDetails,
		jobId,
	});
	return data;
}

/**
 * Returns a function that fetches a role explainer and caches it locally for one
 * hour, keyed by jobId. A repeat request within the hour returns the cached
 * result and does not call the API.
 */
export function useFetchRoleExplainer() {
	const queryClient = useQueryClient();
	return useCallback(
		(payload: CreateRoleExplainerPayload) =>
			queryClient.fetchQuery({
				queryKey: ["role-explainer", payload.jobId],
				queryFn: () => fetchRoleExplainer(payload),
				staleTime: ONE_HOUR,
				gcTime: ONE_HOUR,
			}),
		[queryClient],
	);
}

export type RequirementNecessity = "required" | "preferred";

export interface Requirement {
	item: string;
	necessity: RequirementNecessity;
}

export interface JobRequirements {
	experience: Requirement[];
	skills: Requirement[];
	education: Requirement[];
	certifications: Requirement[];
}

export interface ExtractRequirementsResponse {
	jobId: string;
	jobTitle: string;
	companyName: string;
	requirements: JobRequirements;
}

export function useExtractRequirements() {
	return useMutation<ExtractRequirementsResponse, Error, Record<string, unknown>>({
		mutationFn: async (jobDetails) => {
			const { data } = await fitcheckAPI.post<ExtractRequirementsResponse>("/extract-requirements", {
				jobDetails,
			});
			return data;
		},
	});
}

export type FitStatus = "Fit" | "Miss";

export interface AssessedItem extends Requirement {
	status: FitStatus;
}

export interface AssessedCategory {
	summary: string;
	items: AssessedItem[];
}

export interface FitAssessment {
	experience: AssessedCategory;
	skills: AssessedCategory;
	education: AssessedCategory;
	certifications: AssessedCategory;
}

export interface AssessFitResponse {
	readable: boolean;
	requirements: JobRequirements;
	assessment?: FitAssessment;
}

interface AssessFitPayload {
	resumeURL: string;
	requirements?: JobRequirements;
	jobDetails?: Record<string, unknown>;
	userId?: string;
}

export function useAssessFit() {
	const queryClient = useQueryClient();
	return useMutation<AssessFitResponse, Error, AssessFitPayload>({
		mutationFn: async (payload) => {
			const { data } = await fitcheckAPI.post<AssessFitResponse>("/assess-fit", payload);
			return data;
		},
		onSuccess: (data, variables) => {
			const jobId = (variables.jobDetails as { _id?: string } | undefined)?._id;
			if (variables.userId && jobId) {
				queryClient.invalidateQueries({ queryKey: ["assessment", variables.userId, jobId] });
				queryClient.invalidateQueries({ queryKey: ["assessments", variables.userId] });
			}
		},
	});
}

export interface AssessmentRecord {
	jobId: string;
	roleTitle: string;
	company?: string;
	requirements: JobRequirements;
	assessment: FitAssessment;
	createdAt: string;
	updatedAt: string;
}

export function useAssessment(userId: string, jobId: string) {
	return useQuery<AssessmentRecord | null>({
		queryKey: ["assessment", userId, jobId],
		queryFn: async () => {
			try {
				const { data } = await fitcheckAPI.get<AssessmentRecord>(`/assessment?userId=${userId}&jobId=${jobId}`);
				return data;
			} catch (error) {
				if (axios.isAxiosError(error) && error.response?.status === 404) return null;
				throw error;
			}
		},
		enabled: !!userId && !!jobId,
		staleTime: ONE_HOUR,
		gcTime: ONE_HOUR,
	});
}

interface AssessmentsListResponse {
	success: boolean;
	userId: string;
	count: number;
	assessments: AssessmentRecord[];
}

export function useAssessments(userId: string) {
	return useQuery<AssessmentRecord[]>({
		queryKey: ["assessments", userId],
		queryFn: async () => {
			const { data } = await fitcheckAPI.get<AssessmentsListResponse>(`/assessments/${userId}`);
			return data.assessments;
		},
		enabled: !!userId,
	});
}

export type AnswerValue = "have" | "lack";

export interface FitAnswer {
	item: string;
	category: string;
	question: string;
	answer: AnswerValue;
	answeredAt?: string;
}

interface SaveAnswersPayload {
	userId: string;
	jobId: string;
	answers: FitAnswer[];
}

export function useSaveAnswers() {
	const queryClient = useQueryClient();
	return useMutation<{ answers: FitAnswer[] }, Error, SaveAnswersPayload>({
		mutationFn: async ({ userId, jobId, answers }) => {
			const { data } = await fitcheckAPI.post<{ answers: FitAnswer[] }>("/answers", { userId, jobId, answers });
			return data;
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: ["answers", variables.userId, variables.jobId] });
		},
	});
}

export function useAnswers(userId: string, jobId: string) {
	return useQuery<FitAnswer[]>({
		queryKey: ["answers", userId, jobId],
		queryFn: async () => {
			const { data } = await fitcheckAPI.get<{ answers: FitAnswer[] }>(`/answers?userId=${userId}&jobId=${jobId}`);
			return data.answers;
		},
		enabled: !!userId && !!jobId,
	});
}
